import { Controller } from "@hotwired/stimulus"
import { attachContactSuggest } from "../lib/contact_suggest"
import { track } from "../lib/mixpanel"

// Project Directory (Larry item 19, 2026-09-01): outside contacts, not users.
// Add / edit / remove without leaving the page — the server hands back the
// row and we swap it in place.
// K7-34: typing a company or a name in Add contact offers this company's
// contacts from its other jobs and companies already in ConsLog.
export default class extends Controller {
  static targets = ["modal", "title", "id", "company", "name", "purpose", "phone", "email", "notes",
                    "list", "empty", "saveBtn", "deleteModal", "deleteBtn", "error",
                    "pickNote", "pickNoteBox", "pickNoteText"]
  static values = { createUrl: String, suggestUrl: String, strings: Object, projectId: Number, phase: String }

  connect() {
    if (!this.hasCompanyTarget || !this.suggestUrlValue) return
    const opts = { url: this.suggestUrlValue, strings: this.stringsValue,
                   enabled: () => !this.idTarget.value, onPick: (item, source) => this._picked(item, source) }
    attachContactSuggest(this.companyTarget, { ...opts, kind: "company" })
    attachContactSuggest(this.nameTarget, { ...opts, kind: "name" })
  }

  openNew() {
    this._fill({})
    this.idTarget.value = ""
    this.titleTarget.textContent = "Add contact"
    this._show()
  }

  openEdit(e) {
    const row = e.currentTarget.closest("[data-entry-id]")
    if (!row) return
    this._fill(JSON.parse(row.dataset.entry || "{}"))
    this.idTarget.value = row.dataset.entryId
    this.titleTarget.textContent = "Edit contact"
    this._show()
  }

  hideModal() { this.modalTarget.classList.add("hidden") }
  stopPropagation(e) { e.stopPropagation() }

  async save() {
    const id = this.idTarget.value
    const url = id ? `${this.createUrlValue}/${id}` : this.createUrlValue
    const body = { entry: {
      company_name: this.companyTarget.value, contact_name: this.nameTarget.value, purpose: this.purposeTarget.value,
      phone: this.phoneTarget.value, email: this.emailTarget.value, notes: this.notesTarget.value
    } }
    if (!body.entry.company_name.trim() && !body.entry.contact_name.trim()) return this._error("Add at least a company or a name.")
    this.saveBtnTarget.disabled = true
    try {
      const resp = await fetch(url, { method: id ? "PATCH" : "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not save")
      const existing = this.listTarget.querySelector(`[data-entry-id="${data.id}"]`)
      if (existing) existing.outerHTML = data.html
      else this.listTarget.insertAdjacentHTML("beforeend", data.html)
      this._syncEmpty()
      this.hideModal()
      this._toast(id ? "Contact updated" : "Contact added")
    } catch (err) {
      this._error(err.message)
    } finally {
      this.saveBtnTarget.disabled = false
    }
  }

  confirmDelete(e) {
    this._deleteId = e.currentTarget.closest("[data-entry-id]")?.dataset.entryId
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDelete() { this.deleteModalTarget.classList.add("hidden") }

  async remove() {
    if (!this._deleteId) return
    this.deleteBtnTarget.disabled = true
    try {
      const resp = await fetch(`${this.createUrlValue}/${this._deleteId}`, { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not remove")
      this.listTarget.querySelector(`[data-entry-id="${this._deleteId}"]`)?.remove()
      this._syncEmpty()
      this.hideDelete()
      this._toast("Contact removed")
    } catch (err) {
      this._toast(err.message, "alert")
    } finally {
      this.deleteBtnTarget.disabled = false
    }
  }

  clearPick() {
    this._fill({})
    this.companyTarget.focus()
  }

  _picked(item, source) {
    const s = this.stringsValue
    if (source === "other_jobs") {
      this.companyTarget.value = item.company || ""
      this.nameTarget.value = item.name || ""
      this.purposeTarget.value = item.purpose || ""
      this.phoneTarget.value = item.phone || ""
      this.emailTarget.value = item.email || ""
      this._pickNote((s.filled_from || "").replace("%{job}", item.from || ""), "bg-emerald-50 border-emerald-200 text-emerald-700")
    } else {
      this.companyTarget.value = item.name || ""
      this._pickNote(s.company_from_conslog, "bg-violet-50 border-violet-200 text-violet-700")
      this.nameTarget.focus()
    }
    track("Directory Suggestion Picked", { source, surface: "directory", project_id: this.projectIdValue, project_phase: this.phaseValue })
  }

  _pickNote(text, colors) {
    if (!this.hasPickNoteTarget) return
    this.pickNoteTextTarget.textContent = text || ""
    this.pickNoteBoxTarget.className = `flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-[13px] ${colors}`
    this.pickNoteTarget.classList.remove("hidden")
  }

  _fill(d) {
    this.companyTarget.value = d.company_name || ""
    this.nameTarget.value = d.contact_name || ""
    this.purposeTarget.value = d.purpose || ""
    this.phoneTarget.value = d.phone || ""
    this.emailTarget.value = d.email || ""
    this.notesTarget.value = d.notes || ""
    this.errorTarget.classList.add("hidden")
    if (this.hasPickNoteTarget) this.pickNoteTarget.classList.add("hidden")
  }

  _show() {
    this.modalTarget.classList.remove("hidden")
    setTimeout(() => this.companyTarget.focus(), 50)
  }

  _syncEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", this.listTarget.children.length > 0)
  }

  _error(msg) {
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
  }

  _headers() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token }
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.textContent = message
    document.body.prepend(el)
  }
}
